
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Star, BookOpen } from 'lucide-react';
import { useFoodlist } from '../contexts/FoodlistContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecommendationEngagement = () => {
  const [params] = useSearchParams();
  const type = params.get('type') || 'posts';
  const name = params.get('name') || 'This pick';
  const restaurant = params.get('restaurant') || '';
  const mode = params.get('mode') || 'dine-out';
  const cuisine = params.get('cuisine') || '';
  const vibe = params.get('vibe') || '';
  const rating = params.get('rating') || '4.6';
  const priceRange = params.get('priceRange') || '$$';
  const id = params.get('id') || name || 'item';

  const commentKey = `rec_comments_${id}`;
  const { items, toggleFoodlist, isInFoodlist } = useFoodlist();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState('');

  const backLink = mode ? `/recommendations?comfort=${mode}` : '/recommendations';

  const dishPayload = useMemo(() => ({
    name,
    restaurant,
    location: params.get('location') || '',
    image: params.get('image') || '',
    price: params.get('price') || 0,
    vibe,
    cuisine,
    rating: Number(rating) || 0,
  }), [name, restaurant, params, vibe, cuisine, rating]);

  useEffect(() => {
    if (type !== 'posts') return;
    const tag = cuisine || vibe;
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const query = tag ? `?tag=${encodeURIComponent(tag)}&limit=6` : '?limit=6';
        const res = await fetch(`${API_URL}/api/posts/recommendations${query}`);
        if (res.ok) {
          const data = await res.json();
          setPosts(data.posts || []);
        }
      } catch { /* silent */ } finally { setLoading(false); }
    };
    fetchPosts();
  }, [type, cuisine, vibe]);

  useEffect(() => {
    if (type !== 'comments') return;
    try {
      const stored = JSON.parse(localStorage.getItem(commentKey) || '[]');
      setComments(Array.isArray(stored) ? stored : []);
    } catch { setComments([]); }
  }, [type, commentKey]);

  const addComment = () => {
    if (!commentInput.trim()) return;
    try {
      const current = JSON.parse(localStorage.getItem(commentKey) || '[]');
      const updated = Array.isArray(current) ? [...current, commentInput.trim()] : [commentInput.trim()];
      localStorage.setItem(commentKey, JSON.stringify(updated));
      setComments(updated);
      setCommentInput('');
    } catch { /* silent */ }
  };

  const handleFoodlist = async () => {
    await toggleFoodlist(dishPayload);
  };

  const hasRecipe = mode === 'cook-at-home' || mode === 'meal-plan';
  const recipeItems = ['olive oil', 'garlic', 'seasonal veggies', 'protein of choice', 'herbs'];

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link data-testid="engagement-back-link" to={backLink} className="inline-flex items-center text-sm text-gray-500 hover:text-[#FF6B35]">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Recommendations
          </Link>
          <h1 data-testid="engagement-page-title" className="text-2xl font-bold text-gray-900 mt-4 capitalize">{type}</h1>
          <p className="text-sm text-gray-500 mt-1">{name}{restaurant ? ` · ${restaurant}` : ''}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {type === 'posts' && (
          <div>
            {loading ? (
              <div className="grid md:grid-cols-2 gap-4">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="h-48 rounded-2xl bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div data-testid="engagement-posts-empty" className="text-sm text-gray-500">No posts found yet. Check back soon.</div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {posts.map((post) => (
                  <div key={post.post_id} data-testid={`engagement-post-card-${post.post_id}`} className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                    <div className="aspect-video bg-gray-100">
                      <img
                        src={post.image_url || post.images?.[0]?.url}
                        alt={post.caption || 'Food post'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4 space-y-1">
                      <p className="text-sm font-semibold text-gray-900 line-clamp-2">{post.caption || 'FoodQuest moment'}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-[#FF6B35]" />
                        {post.venue_name || post.location || 'Nearby'}
                      </p>
                      <p className="text-[11px] text-gray-400">{post.reason || post.cuisine || 'Trending now'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {type === 'reviews' && (
          <div className="space-y-4">
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <p className="text-sm font-semibold text-gray-900">Average rating</p>
              <div className="flex items-center gap-2 mt-2">
                <Star className="w-4 h-4 text-[#F4C430]" />
                <span data-testid="engagement-rating" className="text-xl font-bold text-gray-900">{rating}</span>
                <span className="text-sm text-gray-500">· {priceRange}</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">Based on FoodQuest community feedback and recent activity.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                `Loved for its ${vibe || 'comfort'} vibe`,
                `Best for ${cuisine || 'global'} cravings`,
                'Great for date nights and quick catch-ups',
                'Consistent quality and friendly service',
              ].map((item, idx) => (
                <div key={item} data-testid={`engagement-review-item-${idx}`} className="bg-white border border-gray-100 rounded-xl p-4 text-xs text-gray-600">
                  “{item}”
                </div>
              ))}
            </div>
          </div>
        )}

        {type === 'comments' && (
          <div className="space-y-4">
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <p className="text-sm font-semibold text-gray-900 mb-2">Community discussion</p>
              {comments.length === 0 ? (
                <p data-testid="engagement-comments-empty" className="text-xs text-gray-500">No comments yet. Be the first to share your thoughts.</p>
              ) : (
                <div className="space-y-2">
                  {comments.map((comment, idx) => (
                    <div key={`${comment}-${idx}`} data-testid={`engagement-comment-${idx}`} className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2">
                      {comment}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 mt-4">
                <input
                  data-testid="engagement-comment-input"
                  value={commentInput}
                  onChange={(e) => setCommentInput(e.target.value)}
                  placeholder="Add a comment"
                  className="flex-1 px-3 py-2 rounded-full border border-gray-200 text-xs focus:outline-none focus:border-[#FF6B35]"
                />
                <button
                  data-testid="engagement-comment-submit"
                  onClick={addComment}
                  className="px-4 py-2 rounded-full bg-[#FF6B35] text-white text-xs font-semibold"
                >
                  Post
                </button>
              </div>
            </div>
          </div>
        )}

        {type === 'foodlist' && (
          <div className="space-y-4">
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Your Foodlist</p>
                  <p className="text-xs text-gray-500">Save this pick for later collections.</p>
                </div>
                <button
                  data-testid="engagement-foodlist-toggle"
                  onClick={handleFoodlist}
                  className="px-3 py-2 rounded-full border border-[#FF6B35] text-[#FF6B35] text-xs font-semibold"
                >
                  {isInFoodlist(name, restaurant) ? 'Remove' : 'Save'}
                </button>
              </div>
              {items.length === 0 ? (
                <p className="text-xs text-gray-500 mt-4">No foodlist items yet.</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-3 mt-4">
                  {items.slice(0, 6).map((item) => (
                    <div key={`${item.name}-${item.restaurant}`} data-testid={`engagement-foodlist-item-${item.name}`} className="border border-gray-100 rounded-xl p-3 text-xs text-gray-600">
                      <p className="font-semibold text-gray-900 line-clamp-1">{item.name}</p>
                      <p className="text-[11px] text-gray-500 line-clamp-1">{item.restaurant || 'Saved pick'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {type === 'recipes' && (
          <div className="space-y-4">
            <div className="bg-white border border-gray-100 rounded-2xl p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                <BookOpen className="w-4 h-4 text-[#FF6B35]" />Recipe details
              </div>
              <p className="text-xs text-gray-500 mt-2">Quick breakdown for {name}.</p>
              <ul className="grid md:grid-cols-2 gap-2 mt-4">
                {recipeItems.map((item, idx) => (
                  <li key={`${item}-${idx}`} data-testid={`engagement-recipe-item-${idx}`} className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                    {item}
                  </li>
                ))}
              </ul>
              {!hasRecipe && (
                <p className="text-xs text-gray-500 mt-4">Recipes are only available for Prepare It and Meal Plan modes.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecommendationEngagement;
